import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Navigation } from './components';
import FeatureErrorBoundary from './components/common/FeatureErrorBoundary';
import Login from './pages/Login';
import RegisterPage from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Subscription from './pages/Subscription';
import Dashboard from './pages/Dashboard';
import UserData from './features/user/UserData';
import WorkoutPlans from './features/workout/WorkoutPlans';
import DietPlans from './features/diet/DietPlans';
import Progress from './features/progress/Progress';
import ChatWithAI from './features/user/ChatWithAI';

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: string }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md text-center">
            <h2 className="text-xl font-bold text-red-600 mb-3">Something went wrong</h2>
            <p className="text-gray-600 mb-4">{this.state.error}</p>
            <button onClick={() => window.location.reload()} className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function MainLayout() {
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navigation />
      <main className="container mx-auto px-4 py-6 flex-grow">
        <Outlet />
      </main>
      <footer className="bg-white border-t border-gray-200 py-3 text-center text-gray-500 text-xs">
        &copy; {new Date().getFullYear()} Muscle Guide — All Rights Reserved
      </footer>
    </div>
  );
}

function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  const isDev = import.meta.env.DEV;
  const isSubscribed = user?.subscriptionStatus === 'active' || isDev;

  if (user && !isSubscribed && location.pathname !== '/subscription') {
    return <Navigate to="/subscription" replace />;
  }
  return <>{children}</>;
}

function App() {
  const { user, isLoading } = useAuth();
  const [ready, setReady] = useState(false);

  useEffect(() => { setReady(true); }, []);

  if (!ready || isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
        <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/" />} />
        <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" />} />
        <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/" />} />
        <Route path="/subscription" element={user ? <Subscription /> : <Navigate to="/login" />} />
        <Route path="/" element={user ? <SubscriptionGate><MainLayout /></SubscriptionGate> : <Navigate to="/login" />}>
          <Route index element={<FeatureErrorBoundary><Dashboard /></FeatureErrorBoundary>} />
          <Route path="profile" element={<FeatureErrorBoundary><UserData /></FeatureErrorBoundary>} />
          <Route path="workout-plans" element={<FeatureErrorBoundary><WorkoutPlans /></FeatureErrorBoundary>} />
          <Route path="diet-plans" element={<FeatureErrorBoundary><DietPlans /></FeatureErrorBoundary>} />
          <Route path="progress" element={<FeatureErrorBoundary><Progress /></FeatureErrorBoundary>} />
          <Route path="ai-assistant" element={<FeatureErrorBoundary><ChatWithAI /></FeatureErrorBoundary>} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
